import { ObjectiveType, ObjectiveConfigState, ObjectiveConfigKey } from './objective.js';
import { ObjectiveConfig } from './objective-config.js';

/** The maximum number of choices (including the correct one) a
 * multiple-choice question may offer. */
export const MAX_QUIZ_CHOICES = 4;

// serialization format - what gets stored in the config
export interface QuizItemConfigState extends ObjectiveConfigState {
    answer: string;
    /**
     * Multiple-choice options for this question. When present (and non-empty)
     * the question is a multiple-choice question and submissions are validated
     * against this list. The correct answer is the text of one of these choices.
     */
    choices?: string[];
    timestamp: number;
}

export class QuizItemConfig extends ObjectiveConfig {
    timestamp: number;
    type: ObjectiveType = ObjectiveType.answer;
    test: RegExp;

    constructor(
        id: string,
        name: string,
        details: string,
        points: number,
        public answer: string,
        group: string,
        timestamp: number,
        pregame: boolean,
        optional?: boolean,
        sticky?: boolean,
        icon?: string,
        unicode?: string,
        key?: ObjectiveConfigKey | null,
        disabled?: boolean,
        choices?: string[]
    ) {
        super(
            id,
            name,
            details,
            points,
            group,
            pregame,
            undefined,
            optional,
            sticky,
            icon,
            unicode,
            key,
            disabled
        );
        this.timestamp = timestamp;
        this.answer = answer;
        this.choices = choices && choices.length ? choices.slice() : undefined;
        // multiple-choice answers are the exact text of one of the choices,
        // so never interpret them as a regex (a choice like "/etc/hosts"
        // would otherwise compile as a pattern).
        this.test = this.choices
            ? QuizItemConfig.parseLiteralAnswer(this.answer)
            : QuizItemConfig.parseAnswer(this.answer);
    }

    /**
     * The multiple-choice options for this question, or undefined when this is
     * a free-text question.
     */
    choices?: string[];

    static fromState(state: QuizItemConfigState) {
        return new QuizItemConfig(
            state.id,
            state.name,
            state.details,
            state.points,
            state.answer || '',
            state.group,
            state.timestamp,
            state.pregame || false,
            state.optional,
            state.sticky,
            state.icon,
            state.unicode,
            state.key,
            state.disabled,
            state.choices
        );
    }

    /**
     * Validate a choices list for a multiple-choice question.
     * @returns an array of error messages, empty when the choices are valid.
     */
    static validateChoices(choices: string[] | undefined | null): string[] {
        if (!choices || !choices.length) {
            return [];
        }
        const errors: string[] = [];
        if (choices.length > MAX_QUIZ_CHOICES) {
            errors.push(
                `multiple-choice questions support at most ${MAX_QUIZ_CHOICES} choices (got ${choices.length})`
            );
        }
        const seen = new Set<string>();
        for (const choice of choices) {
            if (typeof choice !== 'string' || !choice.trim()) {
                errors.push('choices must be non-empty strings');
            } else if (seen.has(choice)) {
                errors.push(`duplicate choice "${choice}"`);
            } else {
                seen.add(choice);
            }
        }
        return errors;
    }

    /**
     * Validate a quiz answer's configuration: the choices list (when present)
     * and that the correct answer is one of those choices.
     * @returns an array of error messages, empty when the answer is valid.
     */
    static validateAnswer(answer: Pick<QuizItemConfigState, 'id' | 'answer' | 'choices'>) {
        const errors = QuizItemConfig.validateChoices(answer.choices);
        if (
            !errors.length &&
            answer.choices?.length &&
            !answer.choices.includes(answer.answer || '')
        ) {
            errors.push(
                `the correct answer "${answer.answer || ''}" is not one of the configured choices`
            );
        }
        return errors;
    }

    /**
     * Whether a submission is one of the offered choices. Free-text questions
     * (no choices) accept any submission.
     */
    isValidChoice(submission: string): boolean {
        return !this.choices || this.choices.includes(submission);
    }

    static compareAnswers(test: RegExp, input: string) {
        // a g or y flagged RegExp.test() is stateful (lastIndex advances per
        // call), reset it so repeated comparisons are deterministic
        test.lastIndex = 0;
        return test.test(input);
    }

    /**
     * Whether an answer string is in /pattern/flags form, which is parsed as
     * a regex by parseAnswer for free-text questions.
     */
    static isRegexAnswer(answer: string): boolean {
        return typeof answer === 'string' && !!answer.match(/^\/(.+)\/([gimsuy]*)$/);
    }

    /**
     * Parse an answer as an escaped literal, never as a regex. Used for
     * multiple-choice questions where the answer is the exact choice text.
     */
    static parseLiteralAnswer(answer: string): RegExp {
        const escaped = QuizItemConfig.escapeRegex(answer || '');
        return new RegExp(`^${escaped}$`, 'i');
    }

    static parseAnswer(answer: string): RegExp {
        if (typeof answer !== 'string') {
            return answer;
        }
        if (QuizItemConfig.isRegexAnswer(answer)) {
            const isRegexExpression = answer.match(/^\/(.+)\/([gimsuy]*)$/)!;
            // the g and y flags make RegExp.test() stateful (lastIndex) which
            // breaks repeated answer comparisons, and they are meaningless for
            // a single anchored match, so drop them
            const authorFlags = isRegexExpression[2].replace(/[gy]/g, '');
            const flags = authorFlags.includes('i') ? authorFlags : `${authorFlags}i`;
            const pattern = isRegexExpression[1];
            const anchored = `${pattern.startsWith('^') ? '' : '^'}${pattern}${pattern.endsWith('$') ? '' : '$'}`;
            return new RegExp(anchored, flags);
        }
        const escaped = QuizItemConfig.escapeRegex(answer);
        return new RegExp(`^${escaped}$`, 'i');
    }

    static escapeRegex(str: string): string {
        return str.replace(/[$()*+.?[\\\]^{|}]/g, '\\$&');
    }
    toState(): QuizItemConfigState {
        return {
            id: this.id,
            name: this.name,
            details: this.details,
            points: this.points,
            answer: this.answer,
            group: this.group,
            timestamp: this.timestamp,
            pregame: this.pregame,
            optional: this.optional,
            sticky: this.sticky,
            icon: this.icon,
            unicode: this.unicode,
            key: { ...this.key },
            disabled: this.disabled,
            ...(this.choices ? { choices: this.choices.slice() } : {})
        };
    }
}
